import '@elisa-luukku-sites/cms-templates/index.content-blocks.css';
import Templates from '@elisa-luukku-sites/cms-templates';
import { useEffect, useState } from 'react';
import { Link, useRouteLoaderData } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { useIdeatJsonLd } from '../../common/hooks/useIdeatJsonLd.js';
import { useIdeatMeta } from '../../common/hooks/useIdeatMeta.js';
import { allArticlesMsg, keywordsMsg, latestArticlesMsg, t } from '../../common/i18n/index.js';
import { AllArticlesIcon } from '../IdeatCollectionPage/AllArticlesIcon.js';
import { IdeatContentBlocks } from './IdeatContentBlocks.js';
import type { IdeatArticleLoaderData } from '../../common/fetch.js';
import type { IdeatArticleResponse } from '../../generated/api/ideatArticleResponse.js';
import type { IdeatArticleSummary } from '../../generated/api/ideatArticleSummary.js';

import '../IdeatCollectionPage/IdeatCollectionPage.scss';

const { ArticlePage, ArticleGrid } = Templates.default;

const ArticleLink = ({ href, ...props }: { href?: string } & React.ComponentProps<typeof Link>) => (
  <Link {...props} to={href ?? ''} />
);

type IdeatArticlePageProps = {
  article: IdeatArticleResponse;
  latestArticles: IdeatArticleSummary[];
};

export const IdeatArticleContent = ({ article, latestArticles }: IdeatArticlePageProps) => {
  const [isHomeButtonCollapsed, setIsHomeButtonCollapsed] = useState(false);
  useIdeatMeta({ title: article.meta.title, description: article.meta.description, image: article.featuredImageUrl });
  useIdeatJsonLd({
    type: 'article',
    title: article.meta.title,
    description: article.meta.description,
    featuredImageUrl: article.featuredImageUrl,
    publishedOn: article.publishedOn,
    categoryNames: article.categories.map(c => c.title),
    tagNames: article.tags?.map(tag => tag.title) ?? [],
    author: article.author
      ? {
          id: article.author.name,
          name: article.author.name,
          description: article.author.description,
          profileImageUrl: article.author.profileImageUrl,
        }
      : undefined,
  });

  useEffect(() => {
    const handleButtonState = () => {
      setIsHomeButtonCollapsed(window.scrollY > 300);
    };

    handleButtonState();
    document.addEventListener('scroll', handleButtonState);
    return () => {
      document.removeEventListener('scroll', handleButtonState);
    };
  }, []);

  const filteredLatestArticles = latestArticles.filter(a => a.slug !== article.slug).slice(0, 6);

  return (
    <div className="page site cms-page of-ideat-page">
      <div className="buttonContainer">
        <Link className={`allArticlesButton${isHomeButtonCollapsed ? ' smallButton' : ''}`} to={paths.IDEAT}>
          <AllArticlesIcon />
          <span className="buttonText">{t.ZZWR(allArticlesMsg)}</span>
        </Link>
      </div>
      <ArticlePage
        title={article.meta.title}
        featuredImageUrl={article.featuredImageUrl}
        featuredImageAltText={article.featuredImageAltText}
        categoriesBaseUrl={paths.IDEAT_CATEGORIES_BASE}
        shouldDisplayAuthorBox={article.displayAuthorBox}
        canonicalUrl={`${paths.IDEAT}/${article.slug}`}
        author={
          article.author && {
            name: article.author.name,
            description: article.author.description,
            jobTitle: article.author.jobTitle,
            profileImageUrl: article.author.profileImageUrl,
          }
        }
        articleCategories={article.categories.map(category => ({ name: category.title, slug: category.slug }))}
        articleTags={article.tags?.map(tag => ({ name: tag.title, slug: tag.slug }))}
        publishedOn={article.publishedOn}
        linkComponent={Link}
      >
        <IdeatContentBlocks contentBlocks={article.contentBlocks} />
        {article.tags && article.tags.length > 0 && (
          <div className="tagLinkSection">
            <p className="ds-text--l ds-font-weight--bold">{t.ZZWV(keywordsMsg)}</p>
            <div className="tagLinkContainer">
              {article.tags.map(tag => (
                <Link key={tag.slug} className="articleKeywordBadge" to={`${paths.IDEAT_TAGS_BASE}/${tag.slug}`}>
                  {tag.title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </ArticlePage>
      {filteredLatestArticles.length > 0 && (
        <div className="latestArticlesContainer">
          <h2 className="latestArticlesTitle">{t.ZZWU(latestArticlesMsg)}</h2>
          <ArticleGrid
            linkComponent={ArticleLink}
            articles={filteredLatestArticles.map(a => ({
              id: a.slug,
              title: a.title,
              excerpt: a.excerpt,
              date: a.publishedOn,
              imgSrc: a.featuredImageUrl,
              imgAlt: a.featuredImageAltText,
              link: `${paths.IDEAT}/${a.slug}`,
              category: a.category.title,
            }))}
          />
        </div>
      )}
    </div>
  );
};

export const IdeatArticlePath = () => {
  const { article, latestArticles } = useRouteLoaderData('ideat-article-root') as IdeatArticleLoaderData;
  return <IdeatArticleContent article={article} latestArticles={latestArticles.articles} />;
};
